import * as d3 from 'd3';
import Api from '../Api.js';
import Thinker from "../components/Thinker";

const Sunburst = function (options) {
    const b = {
        w: 75, h: 30, s: 3, t: 10
    };
    const colors = [
        "#de783b",
        "#5687d1",
        "#6ab975",
        "#a173d1",
        "#d1cb87",
        "#7b615c"
    ];
    let colorIndex = 0;
    let mappedColors = {};
    let totalSize = 0;
    let radius = null;
    let vis = null;
    let partition = null;
    let arc = null;

    /**
     * Sequence chart functions
     */
    function renderPaths() {
        Thinker.begin($(`#${options.containerName}`));
        const width = 700;
        const height = 650;
        radius = Math.min(width, height) / 2;

        vis = d3.select(options.graphArea).append('svg')
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('id', options.containerName)
            .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

        partition = d3.partition()
            .size([2 * Math.PI, radius * radius]);

        arc = d3.arc()
            .startAngle(function (d) {
                return d.x0;
            })
            .endAngle(function (d) {
                return d.x1;
            })
            .innerRadius(function (d) {
                return Math.sqrt(d.y0);
            })
            .outerRadius(function (d) {
                return Math.sqrt((d.y1));
            });

        Api.send(options.url).done(function (data) {
            Thinker.end($(`#${options.containerName}`));
            const json = buildHierarchy(data);
            createVisualization(json, width);
        });
    }

    function createVisualization(json, width) {
        // Basic setup of page elements.
        initializeBreadcrumbTrail(width);
        drawLegend();

        d3.select("#togglelegend").on("click", toggleLegend);

        // Bounding circle underneath the sunburst, to make it easier to detect
        // when the mouse leaves the parent g.
        vis.append("svg:circle")
            .attr("r", radius)
            .style("opacity", 0);

        // Turn the data into a d3 hierarchy and calculate the sums.
        const root = d3.hierarchy(json)
            .sum(function (d) {
                return d.size;
            })
            .sort(function (a, b) {
                return b.value - a.value;
            });

        // For efficiency, filter nodes to keep only those large enough to see.
        const nodes = partition(root).descendants()
            .filter(function (d) {
                return (d.x1 - d.x0 > 0.005); // 0.005 radians = 0.29 degrees
            });

        vis.data([json]).selectAll("path")
            .data(nodes)
            .enter().append("svg:path")
            .attr("display", function (d) {
                return d.depth ? null : "none";
            })
            .attr("d", arc)
            .attr("fill-rule", "evenodd")
            .style("fill", function (d) {
                return getColor(d.data.name);
            })
            .style("opacity", 1)
            .on("mouseover", mouseover);

        // Add the mouseleave handler to the bounding circle.
        d3.select(`#${options.containerName}`).on("mouseleave", mouseleave);

        // Get total size of the tree = value of root node from partition.
        //totalSize = path.datum().value;
    }

    function mouseover(event, d) {
        const percentage = (100 * d.value / totalSize).toPrecision(3);
        let percentageString = percentage + "%";
        if (percentage < 0.1) {
            percentageString = "< 0.1%";
        }

        d3.select(options.percentageSelector)
            .text(percentageString);

        d3.select(options.explanationSelector)
            .style("visibility", "");

        const sequenceArray = d.ancestors().reverse();
        sequenceArray.shift(); // remove root node from the array
        updateBreadcrumbs(sequenceArray, percentageString);

        // Fade all the segments.
        d3.selectAll(`${options.wrapper} path`)
            .style("opacity", 0.3);

        // Then highlight only those that are an ancestor of the current segment.
        vis.selectAll("path")
            .filter(function (node) {
                return (sequenceArray.indexOf(node) >= 0);
            })
            .style("opacity", 1);
    }

    function mouseleave() {
        // Hide the breadcrumb trail
        d3.select(options.trailSelector)
            .style("visibility", "hidden");

        // Deactivate all segments during transition.
        d3.selectAll(`${options.wrapper} path`).on("mouseover", null);

        // Transition each segment to full opacity and then reactivate it.
        d3.selectAll(`${options.wrapper} path`)
            .transition()
            .duration(1000)
            .style("opacity", 1)
            .on("end", function () {
                d3.select(this).on("mouseover", mouseover);
            });

        d3.select(options.explanationSelector)
            .style("visibility", "hidden");
    }

    function initializeBreadcrumbTrail(width) {
        // Add the svg area.
        const trail = d3.select(options.sequenceSelector).append("svg:svg")
            .attr("width", width)
            .attr("height", 50)
            .attr("class", "trail");
        // Add the label at the end, for the percentage.
        trail.append("text")
            .attr("class", "endlabel")
            .style("fill", "#000");
    }

    // Generate a string that describes the points of a breadcrumb polygon.
    function breadcrumbPoints(d, i) {
        const points = [];
        points.push("0,0");
        points.push(b.w + ",0");
        points.push(b.w + b.t + "," + (b.h / 2));
        points.push(b.w + "," + b.h);
        points.push("0," + b.h);
        if (i > 0) { // Leftmost breadcrumb; don't include 6th vertex.
            points.push(b.t + "," + (b.h / 2));
        }
        return points.join(" ");
    }

    // Update the breadcrumb trail to show the current sequence and percentage.
    function updateBreadcrumbs(nodeArray, percentageString) {

        // Data join; key function combines name and depth (= position in sequence).
        const trail = d3.select(options.trailSelector)
            .selectAll("g")
            .data(nodeArray, function (d) {
                return d.data.name + d.depth;
            });

        // Remove exiting nodes.
        trail.exit().remove();

        // Add breadcrumb and label for entering nodes.
        const entering = trail.enter().append("g");

        entering.append("polygon")
            .attr("points", breadcrumbPoints)
            .style("fill", function (d) {
                return getColor(d.data.name);
            });

        entering.append("text")
            .attr("x", (b.w + b.t) / 2)
            .attr("y", b.h / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "middle")
            .text(function (d) {
                return d.data.name;
            });

        // Merge enter and update selections; set position for all nodes.
        entering.merge(trail).attr("transform", function (d, i) {
            return "translate(" + i * (b.w + b.s) + ", 0)";
        });

        // Now move and update the percentage at the end.
        d3.select(options.trailSelector).select(".endlabel")
            .attr("x", (nodeArray.length + 0.5) * (b.w + b.s))
            .attr("y", b.h / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "middle")
            .text(percentageString);

        // Make the breadcrumb trail visible, if it's hidden.
        d3.select(options.trailSelector)
            .style("visibility", "");
    }

    function getColor(name) {
        if (name === 'end') {
            return '#bbbbbb';
        } else if (mappedColors[name]) {
            return mappedColors[name];
        } else {
            mappedColors[name] = colors[colorIndex % colors.length];
            colorIndex++;
            return mappedColors[name];
        }
    }

    function drawLegend() {
        // Dimensions of legend item: width, height, spacing, radius of rounded rect.
        const li = {
            w: 75, h: 30, s: 3, r: 3
        };

        const legend = d3.select(".navPathLegend").append("svg")
            .attr("width", li.w)
            .attr("height", Object.keys(colors).length * (li.h + li.s));

        const g = legend.selectAll("g")
            .data(Object.entries(colors))
            .enter().append("g")
            .attr("transform", function (d, i) {
                return "translate(0," + i * (li.h + li.s) + ")";
            });

        g.append("svg:rect")
            .attr("rx", li.r)
            .attr("ry", li.r)
            .attr("width", li.w)
            .attr("height", li.h)
            .style("fill", function (d) {
                return d.value;
            });

        g.append("svg:text")
            .attr("x", li.w / 2)
            .attr("y", li.h / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "middle")
            .text(function (d) {
                return d.key;
            });
    }

    function toggleLegend() {
        const legend = d3.select(options.legend);
        if (legend.style("visibility") === "hidden") {
            legend.style("visibility", "");
        } else {
            legend.style("visibility", "hidden");
        }
    }

    function buildHierarchy(csv) {
        const root = {"name": "root", "children": []};
        for (let i = 0; i < csv.length; i++) {
            const sequence = csv[i][0];
            const size = +csv[i][1];
            if (isNaN(size)) { // e.g. if this is a header row
                continue;
            }
            const parts = sequence.split('|');
            let currentNode = root;
            for (let j = 0; j < parts.length; j++) {
                const children = currentNode.children;
                const nodeName = parts[j];
                let childNode;
                if (j + 1 < parts.length) {
                    // Not yet at the end of the sequence; move down the tree.
                    let foundChild = false;
                    for (var k = 0; k < children.length; k++) {
                        if (children[k].name === nodeName) {
                            childNode = children[k];
                            foundChild = true;
                            break;
                        }
                    }
                    // If we don't already have a child node for this branch, create it.
                    if (!foundChild) {
                        childNode = {"name": nodeName, "children": []};
                        children.push(childNode);
                    }
                    currentNode = childNode;
                } else {
                    // Reached the end of the sequence; create a leaf node.
                    childNode = {"name": nodeName, "size": size};
                    children.push(childNode);
                }
            }
            totalSize += size;
        }
        return root;
    }

    renderPaths();
};

export default Sunburst;
